
import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
// EnrichmentResponse is not directly used here, client_enrichment_results schema is used.
import { toast } from 'sonner';

// Polling intervals in milliseconds
const POLLING_INTERVALS = [
  15000,  // 15 seconds
  30000,  // 30 seconds
  60000,  // 1 minute
  120000, // 2 minutes
  300000  // 5 minutes
];

const MAX_POLLING_TIME_MS = 20 * 60 * 1000; // 20 minutes

interface UseIntelligentEnrichmentPollingProps {
  clientId: string | undefined;
  runId: string | undefined;
  isEnabled: boolean;
  onEnrichmentUpdate: (enrichmentData: any) => void;
  onEnrichmentComplete: (enrichmentData: any) => void; // Changed: now passes data
  onEnrichmentError: (errorMessage: string, errorDetails?: any, fullErrorData?: any) => void; // Changed: passes more details
}

export const useIntelligentEnrichmentPolling = ({
  clientId,
  runId,
  isEnabled,
  onEnrichmentUpdate,
  onEnrichmentComplete,
  onEnrichmentError
}: UseIntelligentEnrichmentPollingProps) => {
  const [isPolling, setIsPolling] = useState(false);
  const [currentIntervalIndex, setCurrentIntervalIndex] = useState(0);
  const pollStartTimeRef = useRef<number | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const totalPollsRef = useRef<number>(0);

  useEffect(() => {
    const clearPollingTimer = () => {
      if (pollingTimerRef.current) {
        window.clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };

    const checkEnrichmentStatus = async () => {
      if (!clientId || !runId) return;

      try {
        totalPollsRef.current += 1;
        console.log(`[EnrichmentPoll] Polling attempt ${totalPollsRef.current} for runId: ${runId}, using interval: ${POLLING_INTERVALS[currentIntervalIndex]/1000}s`);

        const { data, error } = await supabase
          .from('client_enrichment_results')
          .select('*')
          .eq('client_id', clientId)
          .eq('run_id', runId)
          .single();

        if (error) {
          console.error('[EnrichmentPoll] Error fetching enrichment status:', error);
          // Potentially call onError if the error is persistent or critical
          // For now, continue polling for transient errors
          // onEnrichmentError(error.message, { type: 'fetch', details: error });
        } else if (data) {
          console.log(`[EnrichmentPoll] Received status update: ${data.status} for runId: ${runId}`);
          
          onEnrichmentUpdate(data);

          if (data.status === 'completed' || data.status === 'error' || data.status === 'applied') {
            if (data.status === 'completed') {
              toast.success("Enrichment data is ready!");
              onEnrichmentComplete(data); // Pass full data object
            } else if (data.status === 'error') {
              toast.error(`Enrichment failed: ${data.error_message || 'Unknown error'}`);
              onEnrichmentError(
                data.error_message || 'Enrichment process failed',
                { stage: data.progress_stage, rawResponse: data }, // errorDetails
                data // fullErrorData
              );
            } else if (data.status === 'applied') {
              // Applied is a form of completion, notify completion with data
              onEnrichmentComplete(data);
            }
            
            setIsPolling(false);
            clearPollingTimer();
            return;
          }
        }

        // Check for timeout
        if (pollStartTimeRef.current && Date.now() - pollStartTimeRef.current > MAX_POLLING_TIME_MS) {
          console.log(`[EnrichmentPoll] Reached maximum polling time (${MAX_POLLING_TIME_MS/60000} minutes). Stopping.`);
          toast.warning(`Enrichment is taking longer than expected. Please check back later.`);
          onEnrichmentError("Polling timed out", { type: 'timeout' });
          setIsPolling(false);
          clearPollingTimer();
          return;
        }

        // Schedule next poll with potentially increased interval
        const nextIntervalIndex = Math.min(currentIntervalIndex + 1, POLLING_INTERVALS.length - 1);
        setCurrentIntervalIndex(nextIntervalIndex);
        
        const nextInterval = POLLING_INTERVALS[nextIntervalIndex];
        pollingTimerRef.current = window.setTimeout(checkEnrichmentStatus, nextInterval);

      } catch (e: any) {
        console.error('[EnrichmentPoll] Unhandled error during polling:', e);
        onEnrichmentError(e.message || 'Unhandled polling exception', { type: 'exception', details: e });
        // Continue polling despite errors, or stop based on error type
        const nextInterval = POLLING_INTERVALS[currentIntervalIndex];
        pollingTimerRef.current = window.setTimeout(checkEnrichmentStatus, nextInterval);
      }
    };

    if (isEnabled && clientId && runId && !isPolling) {
      console.log(`[EnrichmentPoll] Starting intelligent polling for clientId: ${clientId}, runId: ${runId}`);
      setIsPolling(true);
      setCurrentIntervalIndex(0);
      pollStartTimeRef.current = Date.now();
      totalPollsRef.current = 0;
      
      checkEnrichmentStatus();
    }

    return () => clearPollingTimer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, runId, isEnabled, isPolling, currentIntervalIndex, onEnrichmentUpdate, onEnrichmentComplete, onEnrichmentError]);
  // Removed supabase from dependencies as it's stable.

  const stopPolling = () => {
    if (pollingTimerRef.current) {
      window.clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setIsPolling(false);
    console.log('[EnrichmentPoll] Polling manually stopped');
  };

  return {
    isPolling,
    totalPolls: totalPollsRef.current,
    currentInterval: POLLING_INTERVALS[currentIntervalIndex],
    stopPolling
  };
};
